import i18n from "@/i18n.ts";
import { ApiSorting } from "@/types/api.types.ts";
import { ApiDateFilter } from "@/components/date-filter/date-filter.type.ts";
import { ApiPartialDate } from "@/types/common.types.ts";
import { ApiCustomField, ApiCustomFieldValue, ApiField } from "@/types/custom-field.types.ts";
import type {
  ApiEmployeeCompensation,
  ApiEmployeeCompensationPerTypes,
} from "@/types/employees/employee-compensation.types.ts";
import type { ApiEmployeeEmployment } from "@/types/employees/employee-employment.types.ts";
import type { ApiEmployeeAdditionalCompensation } from "@/types/employees/employee-additional-compensation.types.ts";
import type {
  ApiEmployeeColumnFilters,
  ApiEmployeeSearchCustomTable,
} from "@/types/employees/employee-column.types.ts";
import type { ApiEmployeePosition } from "@/types/employees/employee-position.types.ts";
import type { ApiEmployeeTermination } from "@/types/employees/employee-termination.types.ts";
import type { TeamMemberRoleType } from "@/types/team.types.ts";
import { ApiFilterBase, ApiPagedData } from "@skailer-com/uikit/core";
import { ApiCountry } from "@/types/location.types.ts";
import {
  ApiOrganization,
  ApiOrganizationSettingsViewFilter,
  Languages,
} from "@/types/organization.types.ts";
import { ApiApplicantShort } from "@/types/recruit/applicant.types.ts";
import { ApiJobShort } from "@/types/recruit/job.types.ts";
import {
  ApiPermission,
  ApiPermissions,
  ApiRoleShort,
  EmployeeFieldPermission,
  EmployeeFieldPermissions,
  PermissionType,
} from "@/types/role.types.ts";
import { ApiPlan } from "@/types/subscription.types.ts";
import { ApiTimeOffLogFullInformation } from "@/types/time-off/time-off.types.ts";
import { SelectItem } from "@skailer-com/uikit";

export interface ApiAuthorization {
  access_token: string;
  token_type: ApiAuthorizationTokenType;
  next_step: ApiAuthorizationNextStep;
}

export enum ApiAuthorizationTokenType {
  bearer = "bearer",
  tfa_pending = "tfa_pending",
  organization_pending = "organization_pending",
}

export enum ApiAuthorizationNextStep {
  home = "home",
  tfa_code = "tfa_code",
  tfa_setup = "tfa_setup",
  organization_select = "organization_select",
}

export enum EmployeeFullNameRenderType {
  first_last = "first_last",
  last_first = "last_first",
  f_last = "f_last",
}

export enum EmployeeGender {
  male = "male",
  female = "female",
}

export interface ApiEmployeeShort {
  id: number;
  first_name: string;
  last_name: string;
  display_name?: string;
  middle_name?: string;
  avatar_url: string | null;
  background_url?: string;
  is_archived?: boolean;
}

export const avatarSizeToNumberMap = {
  // xs: 60, // 16 px
  sm: 60, // 20 px
  md: 120, // 36 px
  lg: 120, // 48 px
  // xl: 120, // 64 px
  // "2xl": 500, // 160 px
  "4xl": 500, // 224 px
  full: 2000, // 224 px
};

export const getEmployeeAvatarUrl = (
  employee: ApiEmployeeShort,
  size: "sm" | "md" | "lg" | "4xl" | "full"
) => {
  if (!employee.avatar_url) return undefined;

  return employee.avatar_url.replace("{size}", avatarSizeToNumberMap[size].toString());
};

export interface ApiEmployeeWithRoles {
  employee: ApiEmployee;
  roles: ApiRoleShort[];
}

export interface ApiEmployeeSlack {
  name: string;
  handle: string;
  url: string;
  avatar_url: string | null;
}

export interface ApiEmployeeBase extends ApiEmployeeShort {
  email?: string;
  email_personal?: string;
  date_hired_on?: Date;
  date_termination?: Date;
  phone?: string;
  phone_personal?: string;
  slack?: ApiEmployeeSlack | null;
  birth_date?: ApiPartialDate;
  gender?: EmployeeGender;
  color_palette?: string[];
  personnel_number?: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: ApiCountry;
  employee_field_permissions: EmployeeFieldPermissions;
  employee_custom_table_permissions: ApiEmployeeCustomTablePermissions[];
  name_pronunciation?: string;
  timezone?: string;
}

export interface ApiEmployeeCustomTablePermissions {
  effective_from: PermissionType;
  changed_by: PermissionType;
  field_permissions: Record<string, PermissionType>;
  history: PermissionType;
  manage_table: PermissionType;
  table_id: number;
}

export enum ApiEmployeeLifecycleStatus {
  hired = "hired",
  employed = "employed",
  terminated = "terminated",
  // garden_leave = "garden_leave",
  on_leave = "on_leave",
  on_parental_leave = "on_parental_leave",
}

export enum PortalAccessMode {
  preboarding = "preboarding",
  onboarding = "onboarding",
  full = "full",
}

export const getPortalAccessModeTitle = (mode: PortalAccessMode): string => {
  switch (mode) {
    case PortalAccessMode.preboarding:
      return i18n.t("core:portal_access_mode_preboarding");
    case PortalAccessMode.onboarding:
      return i18n.t("core:portal_access_mode_onboarding");
    case PortalAccessMode.full:
      return i18n.t("core:portal_access_mode_full");
  }
};

export const LifecycleStatusTexts = () => {
  return {
    [ApiEmployeeLifecycleStatus.hired]: i18n.t("core:lifecycle_status_hired"),
    [ApiEmployeeLifecycleStatus.employed]: i18n.t("core:lifecycle_status_employed"),
    [ApiEmployeeLifecycleStatus.terminated]: i18n.t("core:lifecycle_status_terminated"),
    [ApiEmployeeLifecycleStatus.on_leave]: i18n.t("core:lifecycle_status_on_leave"),
    [ApiEmployeeLifecycleStatus.on_parental_leave]: i18n.t(
      "core:lifecycle_status_on_parental_leave"
    ),
  };
};

export const shouldAddLifecycleStatusFilterCount = (
  searchParams: URLSearchParams,
  organizationDefaultStatuses: ApiEmployeeLifecycleStatus[] | undefined
): boolean => {
  if (!organizationDefaultStatuses?.length) return false;

  return !searchParams.has("employee_lifecycle_statuses");
};

export const getLifecycleStatusSelectItems = (): SelectItem[] => {
  return [
    {
      id: ApiEmployeeLifecycleStatus.hired,
      title: i18n.t("core:lifecycle_status_hired"),
    },
    {
      id: ApiEmployeeLifecycleStatus.employed,
      title: i18n.t("core:lifecycle_status_employed"),
    },
    {
      id: ApiEmployeeLifecycleStatus.terminated,
      title: i18n.t("core:lifecycle_status_terminated"),
    },
    {
      id: ApiEmployeeLifecycleStatus.on_leave,
      title: i18n.t("core:lifecycle_status_on_leave"),
    },
    {
      id: ApiEmployeeLifecycleStatus.on_parental_leave,
      title: i18n.t("core:lifecycle_status_on_parental_leave"),
    },
  ];
};

export function getActiveEmployeeLifecycleStatuses(): ApiEmployeeLifecycleStatus[] {
  return Object.values(ApiEmployeeLifecycleStatus)
    .filter((status) => status !== ApiEmployeeLifecycleStatus.terminated)
    .sort();
}

export interface ApiEmployeeHiredDetails {
  applicant: ApiApplicantShort;
  job: ApiJobShort;
}

export interface ApiEmployeeWriteToFieldChange {
  field: Omit<ApiField, "section_id" | "is_archived" | "default_value" | "table_id">;
  value_after: ApiCustomFieldValue | null;
  datetime_created_at: Date;
}

export interface ApiEmployeeTeamMembership {
  team_id: number;
  title: string;
  emoji: string;
  role: TeamMemberRoleType;
}

export interface ApiEmployee extends ApiEmployeeBase {
  employee_position: ApiEmployeePosition | null;
  employee_employment: ApiEmployeeEmployment | null;
  employee_termination: ApiEmployeeTermination | null;
  employee_compensation: ApiEmployeeCompensation | null;
  hired_details: ApiEmployeeHiredDetails | null;
  lifecycle_status?: ApiEmployeeLifecycleStatus;
  datetime_last_online?: Date;
  datetime_invited_at?: Date;
  time_off?: ApiTimeOffLogFullInformation;
  custom_fields: ApiCustomField[];
  custom_tables?: ApiEmployeeSearchCustomTable[];
  additional_compensations?: ApiEmployeeAdditionalCompensation[];
  // null — колонки команды не запрошены в load_columns или нет права manage_teams
  teams?: ApiEmployeeTeamMembership[] | null;
  pending_changes?: ApiEmployeeWriteToFieldChange[];
  tfa_enabled: boolean;
  is_banned: boolean | null;
  can_view_employee_folders: boolean;
  can_run_shared_workflows: boolean;
}

export interface ApiImpersonationTokenCreated {
  access_token: string;
  datetime_valid_to: Date;
}

export interface ApiMeImpersonation {
  impersonator_employee_id: number | null;
  impersonator_staff_email: string | null;
  reason: string | null;
  datetime_valid_to: Date;
}

export interface ApiMeEmployee extends ApiEmployeeBase {
  language?: Languages;
  timezone?: string;
  organization_id: number;
  organization: ApiOrganization;
  email_confirmed: boolean;
  available_languages: string[];
  birthday_display?: BirthdayDisplayType;
  theme?: string;
  api_permissions: ApiPermissions;
  other_field_permissions_view: EmployeeFieldPermission[];
  other_field_permissions_edit: EmployeeFieldPermission[];
  plan: ApiPlan;
  extension_display_type: ApiExtensionDisplayTypes | null;
  tfa_enabled: boolean;
  is_password_expired: boolean;
  media_token: string;
  portal_access_mode: PortalAccessMode;
  impersonation: ApiMeImpersonation | null;
}

export interface ApiEmployeeFilter extends ApiFilterBase, ApiEmployeeColumnFilters {
  text?: string;
  manager_ids?: number[];
  division_ids?: number[];
  department_ids?: number[];
  job_profile_ids?: number[];
  job_role_ids?: number[];
  employee_lifecycle_statuses?: ApiEmployeeLifecycleStatus[];
  employment_type_ids?: number[];
  employment_contract_ids?: number[];
  exclude_ids?: number[];
  location_ids?: number[];
  legal_entity_ids?: number[];
  employee_ids?: number[];
  hired_on?: ApiDateFilter | null;
  birthday?: ApiDateFilter | null;
  termination_date?: ApiDateFilter | null;
  me_first?: boolean;
  sorting?: ApiSorting;
  with_child_departments?: boolean;
  edit_api_permissions?: ApiPermission[];
  load_columns?: string[];
}
export interface ApiEmployeeEditPayload {
  last_name?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  email_personal?: string | null;
  email?: string | null;
  date_hired_on?: Date | null;
  phone_personal?: string | null;
  phone?: string | null;
  date_birth?: Date | null;
  gender?: EmployeeGender | string | null;
  personnel_number?: string | null;
  address_1?: string | null;
  address_2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country_id?: number | null;
  name_pronunciation?: string | null;
  custom_fields?: Record<string, ApiCustomFieldValue>;
}

export interface ApiEmployeeBanPayload {
  is_banned: boolean;
}

export interface ApiEmployeeCustomTableEditPayload {
  cells?: Record<string, ApiCustomFieldValue>;
}

export interface ApiEmployeeCustomTableRow {
  id: number;
  date_effective_from: Date | null;
  changed_by: ApiEmployee | null;
  cells: ApiEmployeeCustomTableRowCell[];
  is_current: boolean;
}

export interface ApiEmployeeCustomTableRowCell {
  field: ApiField;
  permission_value: ApiEmployeeCustomTableAcl;
  value: ApiCustomFieldValue;
}

export enum ApiTableRowPendingType {
  added = "added",
  changed = "changed",
}

export interface ApiTableRowPending {
  type: ApiTableRowPendingType;
  datetime_created_at: Date;
}

export interface ApiEmployeeCustomTablePendingRow extends Omit<
  ApiEmployeeCustomTableRow,
  "id" | "is_current"
> {
  id: number | null;
  table_id: number;
  employee: ApiEmployee;
  pending: ApiTableRowPending;
}

export interface ApiEmployeeCustomTableRowPrefill {
  cells: ApiEmployeeCustomTableRowCell[];
}

export interface ApiEmployeeCustomTableSearchResult extends ApiPagedData<ApiEmployeeCustomTableRow> {
  pending_rows: ApiEmployeeCustomTablePendingRow[];
}

export enum ApiEmployeeCustomTableAcl {
  view = "view",
  edit = "edit",
}

export interface ApiEmployeeSetupPayload {
  last_name?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  avatar_file_uuid: string | null;
  division_id: number | null;
  department_id: number | null;
  job_profile_id: number | null;
  location_id: number | null;
  legal_entity_id: number | null;
  reporting_to_id: number | null;
  employment_contract_id: number | null;
  employment_type_id: number | null;
  employment_work_pattern_id: number | null;
  base_salary_gross: string | null;
  base_salary_net: string | null;
  currency: string | null;
  per_type: ApiEmployeeCompensationPerTypes | null;
  candidate_id?: number | null;
  date_hired_on?: Date;
}

export type EmployeeGenderItem = SelectItem;

export const EmployeeGenders: EmployeeGenderItem[] = [
  {
    id: EmployeeGender.male,
    title: "common:gender_male",
  },
  {
    id: EmployeeGender.female,
    title: "common:gender_female",
  },
];

export interface ApiEmployeeAccountSettingsSavePayload {
  language?: Languages;
  timezone?: string;
  birthday_display?: BirthdayDisplayType;
  theme?: string;
  extension_display_type?: ApiExtensionDisplayTypes;
  show_full_salary?: boolean;
}

export const EmployeeFilterDefault: ApiEmployeeFilter = {
  page_number: 1,
  text: undefined,
  employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
};

export enum BirthdayDisplayType {
  full = "full",
  short = "short",
  none = "none",
}

export interface ApiEmployeeTimeOffCategoryPayload {
  date_balance: Date;
  exclude_request_id?: number | undefined;
}

export function getEmployeeFilterFromViewFilter(
  viewFilter: ApiOrganizationSettingsViewFilter
): ApiEmployeeFilter {
  return {
    ...EmployeeFilterDefault,
    division_ids: viewFilter.division_ids,
    department_ids: viewFilter.department_ids,
    job_profile_ids: viewFilter.job_profile_ids,
    job_role_ids: viewFilter.job_role_ids,
    employee_lifecycle_statuses:
      viewFilter.employee_lifecycle_statuses ?? EmployeeFilterDefault.employee_lifecycle_statuses,
    location_ids: viewFilter.location_ids,
    employment_type_ids: viewFilter.employment_type_ids,
    employment_contract_ids: viewFilter.employment_contract_ids,
  };
}

export interface ApiEmployeeInviteLink {
  invite_link: string;
}

export enum ChangePasswordSource {
  inviteEmail = "invite_email",
  inviteLink = "invite_link",
  resetPassword = "reset_password",
  changePassword = "change_password",
}

export enum ApiExtensionDisplayTypes {
  popup = "popup",
  sidePanel = "side_panel",
}

export const getExtensionDisplayTypeSelectItems = (): SelectItem[] => {
  return [
    {
      id: ApiExtensionDisplayTypes.popup,
      title: i18n.t("core:extension_display_type_popup"),
    },
    {
      id: ApiExtensionDisplayTypes.sidePanel,
      title: i18n.t("core:extension_display_type_popup_side_panel"),
    },
  ];
};

export interface ApiTwoFactorSetup {
  secret: string;
  qr_code_base64: string;
  backup_codes: string[];
}

export interface ApiTwoFactorSetupVerifyPayload {
  code: string;
}
